import { useState, useEffect } from 'react';
import { suppliersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Pencil, Trash2, Star, Truck, Sparkles, AlertTriangle } from 'lucide-react';
import { COUNTRIES } from '@/lib/countries';

const SECTORS = ['Steel', 'Aluminum', 'Cement', 'Glass', 'Plastics', 'Metals', 'Chemicals', 'Other'];

function StarRating({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`w-3.5 h-3.5 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
      ))}
    </div>
  );
}

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', country: 'DE', sector: 'Steel', status: 'active', data_quality: 3, pcf_estimate: 0 });
  const [loading, setLoading] = useState(true);

  const fetchSuppliers = () => {
    suppliersAPI.getAll().then(r => { setSuppliers(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchSuppliers(); }, []);

  const filtered = suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  const stats = {
    total: suppliers.length,
    estimated: suppliers.filter(s => s.pcf_estimate > 0).length,
    coverage: suppliers.length > 0 ? Math.round((suppliers.filter(s => s.data_quality >= 3).length / suppliers.length) * 100) : 0,
  };

  const handleSubmit = async () => {
    if (editing) {
      await suppliersAPI.update(editing.id, form);
    } else {
      await suppliersAPI.create(form);
    }
    setDialogOpen(false);
    setEditing(null);
    setForm({ name: '', country: 'DE', sector: 'Steel', status: 'active', data_quality: 3, pcf_estimate: 0 });
    fetchSuppliers();
  };

  const handleDelete = async (id) => {
    await suppliersAPI.delete(id);
    fetchSuppliers();
  };

  return (
    <div data-testid="suppliers-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title">Suppliers</h1>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="estimate-supplier-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Estimate New Supplier (AI)
          </Button>
          <Button onClick={() => { setEditing(null); setForm({ name: '', country: 'Germany', sector: 'Steel', status: 'active', data_quality: 3, pcf_estimate: 0 }); setDialogOpen(true); }}
            className="bg-primary hover:bg-primary/90" data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Supplier
          </Button>
        </div>
      </div>

      {/* AI Disclaimer Banner */}
      <div
        className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
        data-testid="ai-disclaimer-suppliers"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <p>
          <span className="font-semibold">AI estimates are indicative only.</span>{' '}
          Automated PCF values generated from country / sector defaults must be reviewed by a qualified human expert before being used in CBAM filings or audit-ready reports.
        </p>
      </div>

      {/* Mini stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="cp-stat-card animate-fade-in stagger-1" data-testid="stat-total-suppliers">
          <p className="text-xs text-muted-foreground mb-1">Total Suppliers</p>
          <p className="text-xl font-heading font-bold">{stats.total}</p>
        </div>
        <div className="cp-stat-card animate-fade-in stagger-2" data-testid="stat-estimated">
          <p className="text-xs text-muted-foreground mb-1">With PCF Estimates</p>
          <p className="text-xl font-heading font-bold">{stats.estimated}</p>
        </div>
        <div className="cp-stat-card animate-fade-in stagger-3" data-testid="stat-coverage">
          <p className="text-xs text-muted-foreground mb-1">Data Coverage</p>
          <p className="text-xl font-heading font-bold">{stats.coverage}%</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="supplier-search-input" />
      </div>

      <div className="cp-table-wrapper animate-fade-in">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Sector</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Data Quality</TableHead>
              <TableHead className="text-right">PCF Est.</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>{[...Array(7)].map((_, j) => <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" /> No suppliers found
              </TableCell></TableRow>
            ) : filtered.map(s => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.country}</TableCell>
                <TableCell><span className="cp-badge-indigo">{s.sector}</span></TableCell>
                <TableCell>
                  <span className={s.status === 'active' ? 'cp-badge-green' : 'cp-badge-amber'}>{s.status}</span>
                </TableCell>
                <TableCell><StarRating value={s.data_quality} /></TableCell>
                <TableCell className="text-right font-mono font-semibold">{s.pcf_estimate}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(s); setForm({ name: s.name, country: s.country, sector: s.sector, status: s.status, data_quality: s.data_quality, pcf_estimate: s.pcf_estimate }); setDialogOpen(true); }} data-testid={`edit-supplier-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-destructive hover:text-destructive" data-testid={`delete-supplier-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div><Label>Country</Label>
              <Select value={form.country} onValueChange={v => setForm({ ...form, country: v })}>
                <SelectTrigger data-testid="supplier-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sector</Label>
              <Select value={form.sector} onValueChange={v => setForm({ ...form, sector: v })}>
                <SelectTrigger data-testid="supplier-sector-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>PCF Estimate (tCO2/t)</Label><Input type="number" step="0.01" value={form.pcf_estimate} onChange={e => setForm({ ...form, pcf_estimate: Number(e.target.value) })} data-testid="supplier-pcf-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="supplier-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90" data-testid="supplier-submit-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
